import React, { useEffect, useRef, useState } from "react";
import gantt from "dhtmlx-gantt";
import "dhtmlx-gantt/codebase/dhtmlxgantt.css";
import "./GanttChart.scss";
import { IGanttChart } from "@utils/Interfaces";
import { Modal, Flex, DatePicker, InputNumber, Button } from "antd";
import {
  ClockCircleOutlined,
  DeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Helpers from "@utils/Helpers";
const { RangePicker } = DatePicker;

const taskNames = [
  "Sourcing",
  "Contact",
  "Pre-Interview",
  "Interview",
  "Submission",
  "Offering",
  "Onboarding",
];

const taskBarColors = [
  "#A5DFDF",
  "#90d9d9",
  "#7bd1d1",
  "#5ac6c6",
  "#52b4b4",
  "#408d8d",
  "#326d6d",
];

const GanttChart: React.FC<IGanttChart> = ({
  tasks,
  setTasks,
  ganttView,
  startDateAllPhase,
  endDateAllPhase,
  isDateAllPhaseChange,
  setIsDateAllPhaseChange,
}) => {
  const ganttContainer = useRef<HTMLDivElement>(null);
  const [lightboxVisible, setLightboxVisible] = useState(false);
  const [lightboxPosition, setLightboxPosition] = useState({ top: 0, left: 0 });
  const [phasesDays, setPhasesDays] = useState<number[]>([]);
  const [editTask, setEditTask] = useState<any>();

  // HANDLE DISTRIBUTE DAYS
  useEffect(() => {
    if (isDateAllPhaseChange) {
      const formattedStart = Helpers.formattedUTC(startDateAllPhase!, true);
      const formattedEnd = Helpers.formattedUTC(endDateAllPhase!, true);
      // Chuyển đổi chuỗi ngày thành đối tượng Date
      const start = new Date(formattedStart);
      const end = new Date(formattedEnd);
      const numPhases = tasks.length;

      const totalDays = Helpers.calculateDuration(start, end);
      // Tính số ngày mỗi phase và phần dư
      const baseDays = Math.floor(totalDays / numPhases);
      const remainder = totalDays % numPhases;

      // Tạo danh sách số ngày cho từng phase
      const phaseDays = Array.from(
        { length: numPhases },
        (_, i) => baseDays + (i < remainder ? 1 : 0)
      );

      setPhasesDays(phaseDays);
    }
  }, [isDateAllPhaseChange]);

  // HANDLE SET TASKS AFTER DISTRIBUTE
  useEffect(() => {
    if (phasesDays.length > 0) {
      const start = new Date(startDateAllPhase!);
      const newTasks: any[] = [];
      tasks.forEach((item: any, index: number) => {
        const taskItem = {
          id: index + 1,
          text: item.text,
          start_date: `${start.getFullYear()}-${
            start.getMonth() + 1
          }-${start.getDate()}`,
          duration: phasesDays[index],
          color: taskBarColors[index],
          open: true,
        };
        newTasks.push(taskItem);
        start.setDate(start.getDate() + taskItem.duration);
      });
      setTasks(newTasks);
      setIsDateAllPhaseChange(false);
    }
  }, [phasesDays]);

  useEffect(() => {
    const today = new Date();
    const newTasks: any[] = [];
    taskNames.forEach((item, index) => {
      const taskItem = {
        id: index + 1,
        text: item,
        start_date: `${today.getFullYear()}-${today.getMonth() + 1}-${
          today.getDate() + index * 2
        }`,
        duration: 2,
        target_profile: 0,
        color: taskBarColors[index],
        open: true,
      };
      newTasks.push(taskItem);
    });
    setTasks(newTasks);
  }, []);

  useEffect(() => {
    if (ganttContainer.current && tasks.length > 0) {
      // Thiết lập cấu hình Gantt
      gantt.config.date_format = "%Y-%m-%d";
      gantt.config.scroll = true;
      gantt.config.min_column_width = 100;
      gantt.config.row_height = 50;
      gantt.config.bar_height = 30;

      let scale = {};
      switch (ganttView) {
        case "Daily":
          scale = { unit: "day", step: 1, format: "%d %M" };
          break;
        case "Weekly":
          scale = { unit: "week", step: 1, format: "Week %W" };
          break;
        case "Monthly":
          scale = { unit: "month", step: 1, format: "%F %Y" };
          break;
      }
      gantt.config.scales = [scale];

      gantt.showLightbox = function () {
        return null;
      };
      gantt.config.columns = [
        { name: "text", label: "Task Name", width: 240, tree: true },
      ];

      const today = new Date();
      gantt.config.start_date = new Date(tasks[0].start_date);
      gantt.config.end_date = new Date(
        today.getTime() + 30 * 24 * 60 * 60 * 1000
      );

      gantt.templates.grid_row_class = (start: any, end: any, task: any) => {
        return `task-row-color-${task.id}`;
      };

      // Khởi tạo Gantt
      gantt.init(ganttContainer.current);
      gantt.parse({
        data: tasks,
      });

      // Cuộn đến vị trí sau khi khởi tạo
      const scrollState = gantt.getScrollState();
      gantt.scrollTo(scrollState.x, scrollState.y);

      gantt.attachEvent("onTaskClick", function (id, e) {
        const task = gantt.getTask(id);
        setEditTask(task);
        setLightboxPosition({
          top: e.clientY - 100 / 2,
          left: e.clientX - 400 / 2,
        });
        setLightboxVisible(true);
        return false;
      });

      gantt.createDataProcessor(
        (entity: any, action: any, data: any, id: any) => {
          const filtedTasks = [...tasks];
          let prevEndDate: any = null;

          filtedTasks.forEach((item: any, index: number) => {
            if (index > 0) {
              const newStartDate = new Date(prevEndDate);
              const newEndDate = new Date(newStartDate);
              newEndDate.setDate(newEndDate.getDate() + item.duration);

              item.start_date = new Date(newStartDate);
              item.end_date = new Date(newEndDate);
              prevEndDate = item.end_date;
            } else {
              prevEndDate = item.end_date;
            }
          });
          setTasks(filtedTasks);
        }
      );

      return () => {
        gantt.clearAll();
      };
    }
  }, [tasks, ganttView]);

  const handleChangeDuration = (value: any) => {
    if (value != null) {
      const newEditTask = { ...editTask };
      const newEndDate = new Date(newEditTask.start_date);
      newEndDate.setDate(newEndDate.getDate() + value);

      const newStartDate = new Date(newEditTask.start_date);
      newStartDate.setDate(newStartDate.getDate());

      console.log(newStartDate, newEndDate);

      setEditTask({
        ...editTask,
        start_date: new Date(newStartDate),
        end_date: new Date(newEndDate),
        duration: value,
      });
    }
  };

  const handleChangeTargetProfile = (profile: number) => {
    setEditTask({ ...editTask, target_profile: profile == null ? 0 : profile });
  };

  const handleChangeDate = (dateString: any) => {
    const startDate = new Date(dateString[0]);
    const endDate = new Date(dateString[1]);
    const duration = Helpers.calculateDuration(dateString[0], dateString[1]);
    setEditTask({
      ...editTask,
      start_date: startDate,
      end_date: endDate,
      duration: duration,
    });
  };

  const updateMainTask = () => {
    const filterTasks = tasks.map((item: any) =>
      item.id === editTask.id ? editTask : item
    );

    let prevEndDate: any = null;

    filterTasks.forEach((item: any, index: number) => {
      if (index > 0) {
        const newStartDate = new Date(prevEndDate);
        const newEndDate = new Date(newStartDate);
        newEndDate.setDate(newEndDate.getDate() + item.duration);

        item.start_date = new Date(newStartDate);
        item.end_date = new Date(newEndDate);
        prevEndDate = item.end_date;
      } else {
        prevEndDate = item.end_date;
      }
    });
    setTasks(filterTasks);
  };

  return (
    <>
      <div
        className="Heppai-Gantt"
        ref={ganttContainer}
        style={{ width: "100%", height: "405px" }}
      />
      {lightboxVisible && (
        <Modal
          open={lightboxVisible}
          onCancel={() => {
            updateMainTask();
            setLightboxVisible(false);
          }}
          centered={false}
          footer={false}
          closeIcon={false}
          className="gantt-modal"
          style={{
            position: "absolute",
            top: lightboxPosition.top,
            left: lightboxPosition.left,
            transform: "translate(0, 0)",
          }}
        >
          <Flex gap={"small"}>
            <RangePicker
              value={[dayjs(editTask.start_date), dayjs(editTask.end_date)]}
              className="gantt-datepicker"
              onChange={(date, dateString) => handleChangeDate(dateString)}
            />
            <InputNumber
              controls={false}
              size="small"
              value={editTask.duration}
              onChange={(value) => handleChangeDuration(value)}
              className="gantt-input"
              suffix={
                <small className="text-muted">
                  days <ClockCircleOutlined />
                </small>
              }
            />
            <InputNumber
              controls={false}
              type="number"
              size="small"
              value={editTask.target_profile}
              onChange={(value) => handleChangeTargetProfile(value)}
              className="gantt-input"
              suffix={
                <small className="text-muted">
                  profiles <UserOutlined />
                </small>
              }
            />
            <Button
              className="gantt-input-remove"
              icon={<DeleteOutlined className="grantt-remove-btn" />}
            ></Button>
          </Flex>
        </Modal>
      )}
    </>
  );
};

export default GanttChart;
